// @flow
import type { Background, Publishable, Positions } from "./types.js";
import type { JSONContent } from "./jsontypes.js";
import Image from "./image.js";
import Api from "./index.js";
import processPositions from "./positions.js";

export function processMenues(api: Api, _groups: any) {
	if (!_groups) {
		return {};
	}

	const groups = Object.create(null);
	const keys = Object.keys(_groups);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		const menues = _groups[key];
		groups[key] = [];
		for (let i = 0; i < menues.length; i++) {
			const _menu = menues[i];
			const menu = Object.assign({}, _menu);
			menu.items = [];
			groups[key].push(menu);
			for (let i = 0; i < _menu.items.length; i++) {
				const item = Object.assign({}, _menu.items[i]);
				menu.items.push(item);
				if (item.itemType === "collection" || item.itemType === "article") {
					let { publishable, url } = item;
					if (!publishable) {
						// XXX: extracting the id/slug from the url assumes that there are
						// no cross-publisher menu items!
						const split = item.url.split("/");
						publishable = {
							type: item.itemType,
							id: split[split.length - 3],
							slug: split[split.length - 2],
						};
					} else {
						publishable = {
							type: publishable.type,
							id: publishable.lookupId || publishable.id,
							slug: publishable.slug,
						};
					}
					let query = {};
					if (url.indexOf('?') > -1) {
						url.split('?')[1].split('&').forEach(kv => {
							const parts = kv.split('=');
							query[parts[0]] = parts[1];
						})
					}
					item.to = { hierarchy: [publishable], slug: publishable.slug, query };
				} else if (item.itemType === "tagfilter") {
					item.tags = item.tags || item.url.split(",");
					item.to = { query: { tags: item.tags.join(",") } };
				} else if (item.itemType === "tag") {
					item.tags = item.tags || item.url.split(",");
					item.to = { tagged: item.tags };
				} else if (item.itemType === "url" && api.options.previewMode) {
					item.useNewTab = true;
				}
			}
		}
	}
	return groups;
}

export class User {
	_api: Api;

	id: string;
	url: string;
	slug: string;

	type: "publisher" | "user";

	// TODO: this is for compatibility with existing code; remove this later…
	publisher: User;

	name: string;
	image: ?Image;
	loaded: Array<boolean>;

	content: Array<Publishable> | PagedArray<Publishable>;
	positions: Positions;
	positionsPages: Array<Positions>;
	currentPage: ?int;
	pageCount: ?int;

	title: string;
	slogan: string;
	description: string;
	shareDescription: string;
	background: Background;
	menues: { [key: string]: any };
	links: any;
	appearance: any;
	analytics: any;
	tagfilter: Array<string>;
	locales: Array<string>;
	index: ?Publishable;
	canonicalBaseUrl: ?string;
	isSearchIndexingEnabled: ?boolean;

	constructor(api: Api, id: string, json: JSONContent) {
		this._api = api;
		this.id = id;
		this.publisher = this;
		this.loaded = [];
		this.positionsPages = [];
		this.appendPaging = false;
		this.trackCanonical = false;
	}

	setPage(page: int) {
		if (this.url) {
			if (~this.url.indexOf("page=")) {
				this.url = this.url.replace(/page=[0-9]+/, "page=" + page);
			} else {
				this.url += "?page=" + page;
			}
		}

		if (!this.loaded[page]) return false;
		this.currentPage = page;

		if (this.appearance && Array.isArray(this.appearance.styles)) {
			const { styles } = this.appearance;
			const { def } =
				styles.find((s) => s.ns === "Settings" && s.name === "AppendPaging") ||
				{};
			this.appendPaging = def === true;

			const { def: trackCanonical } =
				styles.find(
					(s) => s.ns === "Settings" && s.name === "trackCanonical"
				) || {};
			this.trackCanonical = trackCanonical === true;
		}

		if (
			this.appendPaging &&
			this.positionsPages.reduce((a, c, i) => a + (i <= page ? 1 : 0), 0) ===
				page
		) {
			this.positions = this.positionsPages.reduce((acc, value, idx) => {
				if (idx <= page) {
					acc.main = acc.main || [];
					acc.main.push(
						...value.main.filter(
							(item) =>
								(!item.positioned || idx === 1) &&
								(item.type !== "pagenav" || idx === page)
						)
					);
				}
				return acc;
			}, []);
			(
				this.positions.main.find((item) => item.type === "pagenav") || {}
			).prev = null;
		} else {
			this.positions = this.positionsPages[page];
		}
		return true;
	}

	merge(json: JSONContent) {
		if (
			this.loaded[
				(json.content && json.content.params && json.content.params.page) || 1
			]
		) {
			return;
		}

		this.url = this.url || json.url;
		this.slug = this.slug || json.slug;
		this.type = this.type || (json.type === "user" ? "user" : "publisher");
		this.name = this.name || json.name || "";
		this.title = this.title || json.title || "";
		this.slogan = this.slogan || json.slogan || "";
		this.description = this.description || json.description || "";
		this.shareDescription =
			this.shareDescription || json.shareDescription || "";
		this.appearance = Object.assign({}, this.appearance, json.appearance);
		this.analytics = Object.assign({}, this.analytics, json.analytics);
		this.tagfilter = json.tagfilter || [];
		this.locales = this.locales || [];
		this.canonicalBaseUrl =
			this.canonicalBaseUrl ||
			(json.canonicalBaseUrl &&
				json.canonicalBaseUrl +
					(json.canonicalBaseUrl.endsWith("/") ? "" : "/")) ||
			"";
		this.isSearchIndexingEnabled =
			this.isSearchIndexingEnabled === undefined
				? json.isSearchIndexingEnabled !== false
				: this.isSearchIndexingEnabled;
		if (json.languages && !this.locales.length) {
			this.locales = json.languages;
		}
		if (json.content) {
			if (!json.content.type || json.content.type === "feed") {
				this.currentPage =
					(json.content.params && json.content.params.page) || 1;
				this.pageCount =
					(json.content.params &&
						Math.ceil(json.content.params.total / json.content.params.size)) ||
					1;
			} else {
				this.index = this._api._registerPublishable(json.content.item);
				//this.index.translations = {default: {}};
			}
		}

		if (!this.image && json.image) {
			this.image = new Image(json.image);
		}

		if (
			!this.background &&
			json.background &&
			(json.background.image || json.background.color)
		) {
			const { image, color } = json.background;
			this.background = {
				image: image && new Image(image),
				color,
			};
		}

		this.positions = null;
		if (!this.index && processPositions(this._api, this, json)) {
			this.loaded[this.currentPage] = true;
			this.positionsPages[this.currentPage] = this.positions;
		} else if (this.index) {
			if (this.index.pages) {
				this.loaded = [undefined, ...this.index.pages.map(() => true)];
			} else {
				this.loaded[this.currentPage] = true;
			}
		}

		this.links = json.links || [];
		this.menues = processMenues(this._api, json.menues, json);
		if (json.links && !this.menues.footer && !this.menues.links) {
			const menues = [];
			let links = [];
			let menuName = "";
			this.links.forEach((link) => {
				if (!link[1] || link[1] === "") {
					if (links.length > 0) {
						menues.push({ items: links, name: menuName, title: menuName });
					}
					links = [];
					menuName = link[0];
				} else {
					links.push({
						itemType: "url",
						label: link[0],
						url: link[1],
					});
				}
			});
			if (links.length > 0) {
				menues.push({ items: links, name: menuName, title: menuName });
			}
			if (menues.length > 0) {
				this.menues.footer = this.menues.links = menues;
			}
		}
	}

	load(): Promise<User> {
		return (this._api.resolve(this.id, undefined, this.tagfilter): any);
	}
}



// WEBPACK FOOTER //
// ./lib/api/user.js